import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
// removed withWidth HOC
import { useLocation, Location } from "react-router-dom";
import { ROOT, LOGIN_CONSENT } from "routes";
import { loginAcquire } from "api/LoginAcquire";
import { FC, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Snackbar, Alert, Typography } from "@mui/material";
import FooterLinks from "components/FooterLinks";

interface LocationState {
  /** Original location or path string to return to after login */
  from?: Location | string;
  /** Optional title lingo for specific routes */
  titleLingo?: string;
  /** Legacy flag for early login logic */
  earlyLogin?: boolean;
}

const LoginGateway: FC = (): ReactElement => {
  const location = useLocation();
  // typed router state from useAuthRedirect or axios interceptor
  const state = (location.state ?? {}) as LocationState;
  // also capture `from` in query params (axios interceptor sets ?from=...)
  const params = new URLSearchParams(location.search);
  const fromParam = params.get("from") ?? undefined;
  // const earlyLogin = state.earlyLogin ?? false; // replaced by return-to logic

  const [loginError, setLoginError] = useState<string>("");

  // Handle successful Google login by acquiring session and setting default language
  const { t, i18n } = useTranslation("titleToolbar");
  const onLoginSuccess = (credentialResponse: CredentialResponse) => {
    const token = credentialResponse.credential ?? "";
    // Acquire session with token and browser language
    loginAcquire(token, i18n.language)
      .then((response) => {
        // Determine redirect target: use state.from (Location) or fromParam or fallback
        let target: string;
        if (state.from) {
          if (typeof state.from === "string") {
            target = state.from;
          } else {
            target =
              state.from.pathname +
              (state.from.search ?? "") +
              (state.from.hash ?? "");
          }
        } else if (fromParam) {
          target = decodeURIComponent(fromParam);
        } else {
          target = ROOT;
        }
        // If user has not yet consented, redirect to consent page
        const actorCheck = response.actorMetadata?.actorCheck ?? false;
        if (!actorCheck) {
          // Redirect to consent page with original target preserved
          window.location.replace(
            `${LOGIN_CONSENT}?from=${encodeURIComponent(target)}`,
          );
        } else {
          // Full reload to ensure auth cookie is fully enabled
          window.location.replace(target);
        }
      })
      .catch(onLoginFailure);
  };

  /**
   * Handle login failures by showing a generic error message.
   */
  const onLoginFailure = (_error?: any) => {
    setLoginError("Failed to login!");
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      width="100%"
      sx={{ background: "linear-gradient(to right, #ef233c, #1e40af)" }}
    >
      {/* Main content area: centered */}
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box display="flex">
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 900,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#FFFFFF",
              fontSize: { xs: "6rem", sm: "8rem", md: "10rem", lg: "12rem" },
              lineHeight: 1,
            }}
          >
            {t("title")}
          </Typography>
        </Box>
        {/* Subtitle below title */}
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: "Inter, sans-serif",
            color: "#FFFFFF",
            opacity: 0.9,
            mt: 0,
            lineHeight: 1,
            textAlign: "center",
          }}
        >
          {t("welcomeSubtitle", { ns: "titleWelcome" })}
        </Typography>
        {/* Sign-in button */}
        <Box display="flex" mt={4}>
          <GoogleLogin
            onSuccess={onLoginSuccess}
            onError={onLoginFailure}
            useOneTap
          />
        </Box>
        {/* Error notification */}
        <Box width="100%">
          <Snackbar
            open={loginError !== ""}
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            sx={{ width: "100%" }}
            autoHideDuration={6000}
            onClose={() => setLoginError("")}
          >
            <Alert
              severity="warning"
              variant="filled"
              sx={{ width: "100%" }}
              onClose={() => setLoginError("")}
            >
              {loginError}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
      {/* Footer links */}
      <FooterLinks />
    </Box>
  );
};

export default LoginGateway;
